"use client"

import type React from "react"

import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card } from "@/components/ui/card"
import { useToast } from "@/components/ui/use-toast"
import { sendWelcomeEmail } from "@/integrations/resend/client"

const BrandSetup = () => {
  const { t } = useTranslation("brandSetup")
  const { niche } = useParams()
  const { toast } = useToast()
  const [isLoading, setIsLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    brandName: "",
    logo: null as File | null,
  })

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData((prev) => ({ ...prev, logo: e.target.files![0] }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    console.log("Starting form submission...")

    try {
      if (!formData.logo) {
        throw new Error("Please upload a logo")
      }

      const uploadData = new FormData()
      uploadData.append("image", formData.logo)

      console.log("Sending POST request to analyze-brand...")
      const response = await fetch("https://ukhcptlfuyuwaeohgbvu.supabase.co/functions/v1/analyze-brand", {
        method: "POST",
        body: uploadData,
      })

      console.log("Response status:", response.status)
      if (!response.ok) {
        const errorData = await response.json()
        console.error("Upload failed:", errorData)
        throw new Error(errorData.message || "Failed to analyze brand")
      }

      const data = await response.json()
      console.log("Analysis response:", data)

      // Send welcome email
      await sendWelcomeEmail(formData.email, formData.brandName)

      toast({
        title: t("analysis.success"),
        description: t("analysis.description"),
      })
    } catch (error) {
      console.error("Error during submission:", error)
      toast({
        variant: "destructive",
        title: t("analysis.error"),
        description: error instanceof Error ? error.message : t("analysis.errorDescription"),
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Card className="max-w-2xl mx-auto p-6">
        <h1 className="text-2xl font-bold mb-2">{t("title")}</h1>
        <p className="text-gray-600 mb-6">{t("subtitle")}</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email">{t("form.email")}</Label>
            <Input
              id="email"
              type="email"
              placeholder={t("form.emailPlaceholder")}
              value={formData.email}
              onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="brandName">{t("form.brandName")}</Label>
            <Input
              id="brandName"
              type="text"
              placeholder={t("form.brandNamePlaceholder")}
              value={formData.brandName}
              onChange={(e) => setFormData((prev) => ({ ...prev, brandName: e.target.value }))}
              required
            />
          </div>

          <div>
            <Label htmlFor="logo">{t("form.brandLogo")}</Label>
            <Input id="logo" type="file" accept="image/*" onChange={handleFileChange} required />
            <p className="text-sm text-gray-500 mt-1">{t("form.logoHelp")}</p>
          </div>

          <Button type="submit" disabled={isLoading} className="w-full">
            {isLoading ? t("form.processing") : t("form.submit")}
          </Button>
        </form>
      </Card>
    </div>
  )
}

export default BrandSetup
